import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { T, Icon, stitchGradientBtn, stitchOutlineBtn, stitchDialogPaper } from '../parts/stitch';
import NotitieEditor from '../../../ui-components/NotitieEditor';
import NoteCard from '../parts/NoteCard';
import { useProjectData } from '../ProjectDataContext';

const ROLLEN = ['Projectleider', 'Regisseur', 'Editor', 'Producer', 'Camera'];

function NotesPanel() {
  const {
    id,
    projectNumber,
    notes,
    newNoteText,
    setNewNoteText,
    savingNote,
    handleAddNote,
    handleUpdateNote,
    handleDeleteNote,
  } = useProjectData();
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // ?notitie=<id> (vanuit Correspondentie "✓ Notitie"): naar die notitie scrollen en hem
  // even markeren. Wacht tot hij in de lijst staat — net na het opslaan ververst die nog.
  const [searchParams] = useSearchParams();
  const doelNotitie = searchParams.get('notitie');
  const [gemarkeerd, setGemarkeerd] = useState(null);
  const aanwezig = !!doelNotitie && notes.some((n) => n.id === doelNotitie);
  useEffect(() => {
    if (!aanwezig) return undefined;
    const el = document.getElementById(`notitie-${doelNotitie}`);
    if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setGemarkeerd(doelNotitie);
    const t = setTimeout(() => setGemarkeerd(null), 2500);
    return () => clearTimeout(t);
  }, [aanwezig, doelNotitie]);

  // Rol-bewuste AI-samenvatting over álle notities. De rol onthouden we lokaal;
  // afleiden uit de login kan pas betrouwbaar als rollen per persoon vastliggen.
  const [rolKiezer, setRolKiezer] = useState(false);
  const [rol, setRol] = useState(() => localStorage.getItem('ff_notes_rol') || 'Projectleider');
  const [genereren, setGenereren] = useState(false);
  const [samenvatting, setSamenvatting] = useState(null);
  // Een trage response van project A mag niet in het inmiddels geopende
  // project B landen; en bij projectwissel gaat de oude samenvatting weg.
  const samenvattingSeqRef = useRef(0);
  useEffect(() => { samenvattingSeqRef.current += 1; setSamenvatting(null); setGenereren(false); }, [id]);

  const genereerSamenvatting = async (gekozenRol) => {
    setRolKiezer(false);
    setGenereren(true);
    const seq = ++samenvattingSeqRef.current;
    localStorage.setItem('ff_notes_rol', gekozenRol);
    setRol(gekozenRol);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-notes/samenvatting`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: id, projectNumber, rol: gekozenRol.toLowerCase() }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      if (seq !== samenvattingSeqRef.current) return; // verouderde response (ook A→B→A)
      setSamenvatting({ tekst: d.samenvatting, rol: gekozenRol, aantal: d.aantalNotities });
    } catch (e) { if (seq === samenvattingSeqRef.current) toast.error(`Samenvatting mislukt: ${e.message}`); }
    finally { if (seq === samenvattingSeqRef.current) setGenereren(false); }
  };

  return (
    <>
        {/* Quick add note input — WYSIWYG (1 sep) */}
        <Box sx={{ mb: 3, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <Box sx={{ flex: 1 }}>
            <NotitieEditor value={newNoteText} onChange={setNewNoteText}
              minHeight={110} placeholder="Typ of plak een notitie — opslaan met de + knop" />
          </Box>
          <Box
            component="span"
            onClick={(!newNoteText.trim() || savingNote) ? undefined : handleAddNote}
            sx={{
              ...stitchGradientBtn,
              px: 1.5,
              py: 1,
              alignSelf: 'flex-end',
              height: 40,
              opacity: (!newNoteText.trim() || savingNote) ? 0.5 : 1,
              cursor: (!newNoteText.trim() || savingNote) ? 'default' : 'pointer',
            }}
          >
            {savingNote ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <Icon name="add" size={20} />}
          </Box>
        </Box>

        {/* AI-samenvatting: knop → rolkeuze → resultaatkaart */}
        {notes.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box component="span" onClick={() => (genereren ? null : setRolKiezer((v) => !v))} sx={{
              ...stitchOutlineBtn, display: 'inline-flex', alignItems: 'center', gap: 0.75,
              opacity: genereren ? 0.6 : 1,
            }}>
              {genereren ? <CircularProgress size={16} /> : <Icon name="auto_awesome" size={16} sx={{ color: T.tertiary }} />}
              {genereren ? 'Samenvatten…' : 'AI-samenvatting voor mijn rol'}
            </Box>
            {rolKiezer && !genereren && (
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                {ROLLEN.map((r) => (
                  <Chip key={r} label={r} size="small" onClick={() => genereerSamenvatting(r)}
                    sx={{ fontFamily: T.fontBody, fontWeight: r === rol ? 700 : 500,
                      bgcolor: r === rol ? T.primaryContainer : T.surfaceLow,
                      border: `1.5px solid ${r === rol ? `${T.primary}40` : 'transparent'}`,
                      cursor: 'pointer', '&:hover': { bgcolor: T.surfaceHigh } }} />
                ))}
              </Box>
            )}
            {samenvatting && (
              <Box sx={{ mt: 1.5, p: 2, borderRadius: '0.75rem', bgcolor: `${T.tertiary}0d`, border: `1px solid ${T.tertiary}30` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontFamily: T.fontBody, fontWeight: 800, color: T.tertiary, flex: 1 }}>
                    AI-SAMENVATTING · {samenvatting.rol} · {samenvatting.aantal} notities
                  </Typography>
                  <Box component="span" onClick={() => { navigator.clipboard.writeText(samenvatting.tekst); toast.success('Gekopieerd'); }}
                    sx={{ cursor: 'pointer', display: 'inline-flex' }}><Icon name="content_copy" size={15} sx={{ color: T.outline }} /></Box>
                  <Box component="span" onClick={() => setSamenvatting(null)} sx={{ cursor: 'pointer', display: 'inline-flex' }}>
                    <Icon name="close" size={16} sx={{ color: T.outline }} /></Box>
                </Box>
                <Box sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.text, lineHeight: 1.6,
                  '& h2': { fontSize: '0.85rem', fontWeight: 800, mt: 1.25, mb: 0.5 }, '& ul': { pl: 2.5, my: 0.5 }, '& p': { my: 0.5 } }}>
                  <ReactMarkdown>{samenvatting.tekst}</ReactMarkdown>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {notes.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Icon name="notes" size={40} sx={{ color: T.outlineVariant, display: 'block', mx: 'auto', mb: 1 }} />
            <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.outline }}>Nog geen notities</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {notes.map((note) => (
              <Box key={note.id} id={`notitie-${note.id}`}
                sx={{ borderRadius: '0.75rem', transition: 'box-shadow 0.4s ease',
                  boxShadow: gemarkeerd === note.id ? `0 0 0 2px ${T.primary}` : 'none' }}>
                <NoteCard note={note} onDelete={setConfirmDeleteId} onSave={handleUpdateNote} />
              </Box>
            ))}
          </Box>
        )}

      {/* Bevestiging voor verwijderen — vervangt window.confirm */}
      <Dialog open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)} PaperProps={{ sx: stitchDialogPaper }}>
        <DialogTitle sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '1rem' }}>
          Notitie verwijderen?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.secondary }}>
            Weet je zeker dat je deze notitie wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Box component="span" onClick={() => setConfirmDeleteId(null)} sx={stitchOutlineBtn}>
            Annuleren
          </Box>
          <Box
            component="span"
            onClick={() => { handleDeleteNote(confirmDeleteId); setConfirmDeleteId(null); }}
            sx={{ ...stitchGradientBtn, background: `linear-gradient(135deg, ${T.error} 0%, #8a2e2c 100%)` }}
          >
            <Icon name="delete" size={16} />
            Verwijderen
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default NotesPanel;
